import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import apiHandler from '../handlers/ApiHandler';

// Per-page campaign surfaces resolved by the server. The site sends only the
// page key — the API applies priority, date windows and IP-based geofencing and
// returns the winning `strip` / `popup` / `card` for that page (each may be
// null, meaning "don't render"). Nothing is hardcoded here.
const CampaignContext = createContext({
  strip: null,
  popup: null,
  card: null,
  loading: true,
  stripVisible: false,
});

// Map the current route to the page key the resolve API expects.
export const pageKeyFromPath = (pathname) => {
  if (pathname === '/') return 'home';
  if (pathname === '/products') return 'products';
  if (/^\/products\/[^/]+/.test(pathname)) return 'product';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/about') return 'about';
  if (pathname === '/news' || pathname.startsWith('/news/')) return 'news';
  return 'home';
};

export const CampaignProvider = ({ children }) => {
  const location = useLocation();
  const key = pageKeyFromPath(location.pathname);

  const [strip, setStrip] = useState(null);
  const [popup, setPopup] = useState(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Re-resolve on mount and whenever the page key changes. While a request is
  // in flight (or if it fails) every surface is null so there's no flash of
  // stale campaign content when navigating between pages.
  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setStrip(null);
    setPopup(null);
    setCard(null);

    apiHandler
      .get('/campaigns/resolve?page=' + encodeURIComponent(key))
      .then((response) => {
        if (!mounted) return;
        setStrip(response && response.strip ? response.strip : null);
        setPopup(response && response.popup ? response.popup : null);
        setCard(response && response.card ? response.card : null);
      })
      .catch(() => {
        if (!mounted) return;
        setStrip(null);
        setPopup(null);
        setCard(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [key]);

  const stripVisible = !!strip;

  const value = { strip, popup, card, loading, stripVisible };

  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
};

export const useCampaign = () => useContext(CampaignContext);

export default CampaignContext;
